import cn from 'classnames';
import type { MouseEventHandler } from 'react';
import React, { memo, useCallback } from 'react';

import { Space, Typography } from '@sravni/react-design-system';
import { generateKeyFromTab } from '@sravni/ugc/lib/specialOffers/helpers';

import { schemaToElement } from '../../../../../../../helpers/schemaToElement';
import { trimText } from '../../../../../../../helpers/trim';
import blockStyles from '../../../../../block.module.scss';
import type { MainBlockProps } from '../../components/MainBlock';

import styles from './common.module.scss';

export interface CommonProps
  extends Pick<
    MainBlockProps,
    | 'activeTabType'
    | 'bankName'
    | 'logoBlock'
    | 'isMobile'
    | 'conditions'
    | 'isWithButtons'
    | 'offerTitle'
    | 'handleOfferTitleClick'
  > {
  itemId: string;
}

export const Common = memo(
  ({
    activeTabType,
    bankName,
    logoBlock,
    isMobile,
    itemId,
    conditions,
    isWithButtons,
    offerTitle,
    handleOfferTitleClick,
  }: CommonProps) => {
    const onOfferTitleClick: MouseEventHandler = useCallback(
      (e) => {
        e.stopPropagation();
        handleOfferTitleClick?.();
      },
      [handleOfferTitleClick],
    );

    return (
      <>
        <Space direction="horizontal" justify="space-between">
          <Space direction="vertical" justify="center" className={blockStyles.header}>
            <Typography.Heading level={5} className={cn('h-mb-4', 'h-color-D100', blockStyles.clampOneLine)}>
              {bankName}
            </Typography.Heading>
          </Space>
          <div className={cn(blockStyles.logo, blockStyles.logoCommon)}>{logoBlock}</div>
        </Space>

        <Typography.Text
          className={cn('h-mt-16', styles.clampOneLine)}
          size={10}
          uppercase
          strong
          dangerouslySetInnerHTML={trimText(offerTitle, 24, true)}
          onClick={onOfferTitleClick}
        />
        <div
          className={cn(
            styles.conditions,
            'h-mt-12',
            isWithButtons && isMobile && 'h-mb-12',
            isWithButtons && !isMobile && 'h-mb-16',
          )}
        >
          {conditions &&
            conditions.map(
              ({ value, description, wide, SOValue }, i) =>
                (value || SOValue) && (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${generateKeyFromTab(activeTabType)}-${itemId}-conditions-${value}-${i}`}
                    className={cn(styles.conditions__item, { [styles['conditions__item--wide']]: wide })}
                  >
                    <Typography.Heading level={5} className="h-color-D100">
                      {SOValue ? schemaToElement(SOValue) : value}
                    </Typography.Heading>
                    <Typography.Text size={12} className="h-mt-4 h-color-D30">
                      {description}
                    </Typography.Text>
                  </div>
                ),
            )}
        </div>
      </>
    );
  },
);
